import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Users, CalendarRange, BarChart3 } from "lucide-react";
import { listarTarefas, Tarefa } from "../services/tarefas";
import { listarClientes } from "../services/clientes";
import { useAuth } from "../hooks/useAuth";

// Parseia a parte de DATA (yyyy-mm-dd) como meia-noite LOCAL. Datas do backend
// vêm como ISO UTC ("...T00:00:00.000Z"); usar new Date() direto desloca um dia
// em fusos negativos (UTC-3), jogando a entrega pro dia anterior na grade.
function parseDataLocal(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// MELHORIA 16 — cor estável por cliente (estilo Google Agenda): mesmo cliente
// sempre a mesma cor. Hash simples do id → paleta.
const PALETA_CLIENTE = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#c026d3", "#0ea5e9", "#e11d48"];
function corCliente(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETA_CLIENTE[h % PALETA_CLIENTE.length];
}

interface Entrega {
  tarefa: Tarefa;
  data: Date;
  /** true = data combinada com o cliente (dataEntregaCliente); false = estimada (caiu no prazo interno). */
  combinada: boolean;
  clienteId: string;
  clienteNome: string;
  cor: string;
  icone: string;
  tipoLabel: string;
}

// Só projetos EXTERNOS com cliente vinculado entram na pauta — projeto interno
// não tem "cliente" pra reportar.
function montarEntregas(tarefas: Tarefa[]): Entrega[] {
  const out: Entrega[] = [];
  for (const t of tarefas) {
    const cliente = t.projeto?.clienteRef;
    if (!cliente || t.projeto?.interno) continue;
    const dataStr = (t as any).dataEntregaCliente || t.prazo;
    if (!dataStr) continue;
    out.push({
      tarefa: t,
      data: parseDataLocal(dataStr),
      combinada: !!(t as any).dataEntregaCliente,
      clienteId: cliente.id,
      clienteNome: cliente.nome,
      cor: t.tipoTarefa?.cor || "#64748b",
      icone: t.tipoTarefa?.icone || "🗂️",
      tipoLabel: t.tipoTarefa?.label || "Sem tipo",
    });
  }
  return out;
}

type Modo = "semana" | "cliente" | "balanco";
const HEADER_DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export function Entregas() {
  const { can } = useAuth();
  const podeVer = can(["gestor", "admin"]);
  const hoje = new Date();

  const [modo, setModo] = useState<Modo>("semana");
  const [referencia, setReferencia] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  const [clienteId, setClienteId] = useState("");
  // MELHORIA 16 — pauta: ver SÓ as datas combinadas com o cliente (corta o
  // fallback pro prazo interno) e colorir por cliente em vez de por tipo.
  const [soCombinadas, setSoCombinadas] = useState(false);
  const [corPor, setCorPor] = useState<"tipo" | "cliente">("tipo");

  const { data: tarefas = [], isLoading } = useQuery({
    queryKey: ["tarefas-entregas"],
    queryFn: () => listarTarefas(),
    enabled: podeVer,
  });
  const { data: clientes = [] } = useQuery({
    queryKey: ["clientes-entregas"],
    queryFn: () => listarClientes(),
    enabled: podeVer,
  });

  const entregas = useMemo(() => montarEntregas(tarefas), [tarefas]);
  // Base após o filtro "só combinadas": quando ligado, corta as estimadas (que
  // caíram no prazo interno) e deixa SÓ o que foi acordado com o cliente.
  const entregasBase = useMemo(
    () => (soCombinadas ? entregas.filter((e) => e.combinada) : entregas),
    [entregas, soCombinadas],
  );
  const hojeIso = ymd(hoje);

  // Range visível conforme o modo: semana (7 dias) ou mês (cliente/balanço).
  const { dias, primeiroDia, ultimoDia, label } = useMemo(() => {
    if (modo === "semana") {
      const dow = referencia.getDay();
      const ini = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate() - dow);
      const fim = new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + 6);
      const arr = Array.from({ length: 7 }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
      return {
        dias: arr,
        primeiroDia: ini,
        ultimoDia: fim,
        label: `${ini.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${fim.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`,
      };
    }
    // cliente/balanço: grade padronizada em semanas completas do mês
    const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
    const ultimo = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 0);
    const ini = new Date(primeiro.getFullYear(), primeiro.getMonth(), primeiro.getDate() - primeiro.getDay());
    const fim = new Date(ultimo.getFullYear(), ultimo.getMonth(), ultimo.getDate() + (6 - ultimo.getDay()));
    const total = Math.round((fim.getTime() - ini.getTime()) / 86400000) + 1;
    const arr = Array.from({ length: total }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
    return {
      dias: arr,
      primeiroDia: primeiro,
      ultimoDia: ultimo,
      label: primeiro.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    };
  }, [modo, referencia]);

  function navegar(delta: number) {
    const d = new Date(referencia);
    if (modo === "semana") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setReferencia(d);
  }
  function irParaHoje() {
    setReferencia(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  }

  // "Semana" mostra todos os clientes juntos; "Por cliente" filtra ao selecionado.
  const entregasFiltradas = useMemo(() => {
    if (modo === "cliente") return clienteId ? entregasBase.filter((e) => e.clienteId === clienteId) : [];
    return entregasBase;
  }, [entregasBase, modo, clienteId]);

  const entregasPorDia = useMemo(() => {
    const mapa = new Map<string, Entrega[]>();
    for (const e of entregasFiltradas) {
      const k = ymd(e.data);
      const arr = mapa.get(k) || [];
      arr.push(e);
      mapa.set(k, arr);
    }
    for (const arr of mapa.values()) {
      arr.sort((a, b) => (modo === "semana" ? a.clienteNome.localeCompare(b.clienteNome) : a.tarefa.titulo.localeCompare(b.tarefa.titulo)));
    }
    return mapa;
  }, [entregasFiltradas, modo]);

  // Balanço: total de entregas do MÊS (sem padding de semana) por cliente.
  const balanco = useMemo(() => {
    if (modo !== "balanco") return null;
    const doMes = entregasBase.filter((e) => e.data >= primeiroDia && e.data <= ultimoDia);
    const porCliente = new Map<string, { nome: string; qtd: number }>();
    for (const e of doMes) {
      const atual = porCliente.get(e.clienteId) || { nome: e.clienteNome, qtd: 0 };
      atual.qtd++;
      porCliente.set(e.clienteId, atual);
    }
    const linhas = Array.from(porCliente.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.qtd - a.qtd);
    const max = linhas.reduce((m, l) => Math.max(m, l.qtd), 0);
    return { total: doMes.length, linhas, max };
  }, [entregasBase, modo, primeiroDia, ultimoDia]);

  // Legenda dinâmica: por TIPO de peça ou por CLIENTE, conforme `corPor`.
  const legenda = useMemo(() => {
    const base = modo === "balanco" ? entregasBase.filter((e) => e.data >= primeiroDia && e.data <= ultimoDia) : entregasFiltradas;
    const mapa = new Map<string, { label: string; cor: string }>();
    for (const e of base) {
      if (corPor === "cliente") mapa.set(e.clienteId, { label: e.clienteNome, cor: corCliente(e.clienteId) });
      else mapa.set(e.tipoLabel, { label: e.tipoLabel, cor: e.cor });
    }
    return Array.from(mapa.values()).sort((a, b) => a.label.localeCompare(b.label)).slice(0, 14);
  }, [entregasFiltradas, entregasBase, modo, corPor, primeiroDia, ultimoDia]);

  if (!podeVer) {
    return <div className="text-slate-500 text-sm">Acesso restrito à gestão.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
          <button
            onClick={() => setModo("semana")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${modo === "semana" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
          >
            <CalendarRange size={12} className="mr-1" /> Semana (todos)
          </button>
          <button
            onClick={() => setModo("cliente")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${modo === "cliente" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
          >
            <Users size={12} className="mr-1" /> Por cliente
          </button>
          <button
            onClick={() => setModo("balanco")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${modo === "balanco" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
          >
            <BarChart3 size={12} className="mr-1" /> Balanço do mês
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {modo === "cliente" && (
            <select
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg px-2.5 py-2"
            >
              <option value="">Selecione um cliente…</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-1.5">
            <button onClick={() => navegar(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200" title="Anterior">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize px-3 min-w-[11rem] text-center">{label}</span>
            <button onClick={() => navegar(1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200" title="Próximo">
              <ChevronRight size={16} />
            </button>
            <button onClick={irParaHoje} className="ml-1 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">
              Hoje
            </button>
          </div>
        </div>
      </div>

      {/* MELHORIA 16 — pauta: filtrar só combinadas com o cliente + cor por cliente. */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button
          onClick={() => setSoCombinadas((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium border ${soCombinadas ? "bg-blue-600 border-blue-600 text-white" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-blue-400"}`}
          title="Mostra SÓ o que foi combinado com o cliente (esconde as datas estimadas pelo prazo interno)"
        >
          🔒 Só datas combinadas com o cliente
        </button>
        <span className="text-slate-400 dark:text-slate-500">·</span>
        <span className="text-slate-500 dark:text-slate-400">Cor por:</span>
        <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
          {(["cliente", "tipo"] as const).map((op) => (
            <button
              key={op}
              onClick={() => setCorPor(op)}
              className={`px-2.5 py-1 rounded-md font-medium capitalize ${corPor === op ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
            >
              {op}
            </button>
          ))}
        </div>
      </div>

      {modo !== "balanco" && legenda.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
          <span className="text-slate-500 dark:text-slate-400 mr-1">{corPor === "cliente" ? "Clientes:" : "Tipos:"}</span>
          {legenda.map((t) => (
            <span key={t.label} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: t.cor }} />
              {t.label}
            </span>
          ))}
          {!soCombinadas && (
            <>
              <span className="text-slate-400 dark:text-slate-500 ml-2">·</span>
              <span className="text-slate-500 dark:text-slate-400">borda sólida = combinada · tracejada = estimada (prazo interno)</span>
            </>
          )}
        </div>
      )}

      {isLoading && <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center text-slate-400 dark:text-slate-500 text-sm">Carregando entregas…</div>}

      {!isLoading && modo === "cliente" && !clienteId && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-10 text-center">
          <Users size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Selecione um cliente para ver a pauta do mês.</p>
        </div>
      )}

      {!isLoading && modo === "balanco" && balanco && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">{balanco.total}</span>{" "}
            entrega{balanco.total === 1 ? "" : "s"} prevista{balanco.total === 1 ? "" : "s"} em <span className="capitalize font-medium">{label}</span>
          </p>
          {balanco.linhas.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">Nenhuma entrega prevista neste mês.</p>}
          <div className="space-y-2.5">
            {balanco.linhas.map((l) => (
              <button
                key={l.id}
                onClick={() => { setClienteId(l.id); setModo("cliente"); }}
                className="w-full text-left group"
                title="Ver pauta deste cliente"
              >
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">{l.nome}</span>
                  <span className="text-slate-500 dark:text-slate-400">{l.qtd}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${balanco.max ? (l.qtd / balanco.max) * 100 : 0}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!isLoading && (modo === "semana" || (modo === "cliente" && clienteId)) && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
            {HEADER_DIAS.map((d, i) => {
              const ehFds = i === 0 || i === 6;
              return (
                <div key={d} className={`px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide ${ehFds ? "bg-slate-200 dark:bg-slate-900/70 text-slate-500 dark:text-slate-500" : "bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-300"}`}>
                  {d}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7">
            {dias.map((dia, idx) => {
              const ehHoje = ymd(dia) === hojeIso;
              const ehMesAtual = modo === "cliente" ? dia.getMonth() === referencia.getMonth() : true;
              const dow = dia.getDay();
              const ehFds = dow === 0 || dow === 6;
              const doDia = entregasPorDia.get(ymd(dia)) || [];
              const maxVisiveis = modo === "semana" ? 8 : 3;
              const visiveis = doDia.slice(0, maxVisiveis);
              const restantes = doDia.length - visiveis.length;
              const bg = ehFds ? "bg-slate-100 dark:bg-slate-900/60" : ehMesAtual ? "bg-white dark:bg-slate-800" : "bg-slate-50/70 dark:bg-slate-900/40";

              return (
                <div
                  key={idx}
                  className={`border-b border-r border-slate-100 dark:border-slate-700 ${idx % 7 === 0 ? "border-l" : ""} ${modo === "semana" ? "min-h-[240px]" : "min-h-[110px]"} p-1.5 flex flex-col gap-0.5 ${bg}`}
                >
                  <div className="flex items-center justify-between px-1">
                    <span className={`text-[11px] font-medium inline-flex items-center justify-center ${ehHoje ? "bg-blue-600 text-white rounded-full w-6 h-6" : ehFds ? "text-slate-400 dark:text-slate-500" : ehMesAtual ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-500"}`}>
                      {dia.getDate()}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {visiveis.map((e) => (
                      <Link
                        key={e.tarefa.id}
                        to={`/tarefas?tarefa=${e.tarefa.id}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded truncate font-medium text-white hover:opacity-90 transition-opacity flex items-center gap-1 ${e.combinada ? "border border-white/30" : "border border-dashed border-white/60 opacity-80"}`}
                        style={{ background: corPor === "cliente" ? corCliente(e.clienteId) : e.cor }}
                        title={`${e.tipoLabel}: ${e.tarefa.titulo} · ${e.clienteNome}${e.combinada ? " (data combinada)" : " (estimada pelo prazo interno)"}`}
                      >
                        <span className="shrink-0">{e.icone}</span>
                        {modo === "semana" && <span className="truncate">{e.clienteNome} ·</span>}
                        <span className="truncate">{e.tarefa.titulo}</span>
                      </Link>
                    ))}
                    {restantes > 0 && <span className="text-[10px] px-1.5 py-0.5 text-slate-500 dark:text-slate-400 font-medium">+{restantes} mais</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
